import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CheckServiceability from "./CheckServiceability";
import RateCalculator from "./RateCalculator";
import UploadProductSkuMaster from "./UploadProductSkuMaster";

const VALID_TOOLS = ["serviceability", "calculator", "skuMaster"];

const Tools = () => {
    // Allow deep-linking to a specific tool via ?tab= (e.g. /tools?tab=skuMaster).
    const initialTool = (() => {
        try {
            const tab = new URLSearchParams(window.location.search).get("tab");
            return VALID_TOOLS.includes(tab) ? tab : "serviceability";
        } catch (e) {
            return "serviceability";
        }
    })();
    const [activeTool, setActiveTool] = useState(initialTool);

    const tools = [
        {
            id: "serviceability",
            name: "Check Serviceability",
            icon: "🔍",
            description: "Verify courier availability for your route",
        },
        {
            id: "calculator",
            name: "Rate Calculator",
            icon: "💰",
            description: "Get instant shipping cost estimates",
        },
        {
            id: "skuMaster",
            name: "SKU Product Master",
            icon: "📦",
            description: "Upload or manage your product catalog",
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 mt-2">
            <div className="max-w-[1920px] mx-auto px-2 lg:px-3 pb-4">
                {/* Header Section */}
                

                {/* Enhanced Tab Navigation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mb-4 sm:mb-6"
                >
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-1.5 sm:p-2 flex flex-col sm:inline-flex sm:flex-row gap-2 w-full sm:w-auto">
                        {tools.map((tool, index) => (
                            <motion.button
                                key={tool.id}
                                onClick={() => setActiveTool(tool.id)}
                                className={`relative px-4 py-3 sm:px-6 sm:py-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 sm:gap-3 min-h-[56px] sm:min-h-0 ${
                                    activeTool === tool.id
                                        ? "text-white"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                                {activeTool === tool.id && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg sm:rounded-xl shadow-md"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-10 text-lg">{tool.icon}</span>
                                <div className="relative z-10 text-left flex-1">
                                    <div className="font-bold text-sm sm:text-base">{tool.name}</div>
                                    <div className={`text-xs ${activeTool === tool.id ? "text-blue-100" : "text-gray-500"} hidden sm:block`}>
                                        {tool.description}
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                {/* Tool Content with Smooth Transitions */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTool}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                    >
                        {activeTool === "serviceability" && <CheckServiceability />}
                        {activeTool === "calculator" && <RateCalculator />}
                        {activeTool === "skuMaster" && <UploadProductSkuMaster />}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Tools;
